/*
 * Copyright (c) Worldline 2023.
 */

import {Component, OnInit} from '@angular/core';
import {NgxSpinnerService} from "ngx-spinner";
import {CancelService} from "../../services/cancel.service";
import {ActivatedRoute} from "@angular/router";
import {RootScopeService} from "../../services/rootscope.service";
import {Steps} from "../../constant/steps";
import {RedirectionService} from "../../services/redirection.service";
import {SessionService} from "../../services/session.service";
import {SignerService} from "../../services/signer.service";
import {SignerDto} from "../../model/signerDto";
import {SessionDto} from "../../model/sessionDto";
import {SharedService} from "../../services/shared.service";
import {HttpService} from "../../http/http.service";
import ExternalSignerStatusEnum = SignerDto.ExternalSignerStatusEnum;
import ExternalSessionStatusEnum = SessionDto.ExternalSessionStatusEnum;

@Component({
    selector: 'app-signatures',
    templateUrl: './signatures.component.html'
})
export class SignaturesComponent implements OnInit {

    constructor(private spinnerService: NgxSpinnerService,
                private cancelService: CancelService,
                private route: ActivatedRoute,
                private rootScope: RootScopeService,
                private redirectionService: RedirectionService,
                private sessionService: SessionService,
                private signersService: SignerService,
                private sharedService: SharedService,
                private http: HttpService) {
    }

    errorMsg: any;
    spinneractive = false;

    private delay = 3000;
    private sessionId;

    ngOnInit(): void {
        console.debug("Init SignaturesComponent")
        let that = this;
        this.rootScope.get().step = Steps.signatures;

        this.delay = 3000;
        if (this.rootScope.get().properties.signature.concurrentPollingDelay) {
            this.delay = this.rootScope.get().properties.signature.concurrentPollingDelay;
        }

        // Check the sessionId
        this.sessionId = this.route.snapshot.params['sessionId'];

        if (this.sessionId !== this.sessionService.id) {
            console.warn('The session ID in the url is not the same as the saved : url->' + this.sessionId + ' | saved->' + this.sessionService.id);
            this.redirectionService.session(this.route, this.sessionId);
            return;
        }

        // Check the status of the session
        if (typeof this.sessionService.info === 'undefined' || this.sessionService.info.externalSessionStatus !== ExternalSessionStatusEnum.Init) {
            console.warn('The current signer is undefined or is not at the init status');
            this.redirectionService.session(this.route, this.sessionId);
            return;
        }

        // Check the status of the signer
        if (typeof this.signersService.currentSigner === 'undefined' ||
            (this.signersService.currentSigner.externalSignerStatus !== ExternalSignerStatusEnum.Signatures &&
                this.signersService.currentSigner.externalSignerStatus !== ExternalSignerStatusEnum.CertificateApproval)) {
            console.warn('The current signer is undefined or is not at the agreements status');
            this.redirectionService.session(this.route, this.sessionId);
            return;
        }

        this.spinneractive = false;
        this.sharedService.on('us-spinner:spin', function () {
            that.spinneractive = true;
        });
        this.sharedService.on('us-spinner:stop', function () {
            that.spinneractive = false;
        });

        console.debug('The context is OK.');

        this.sign();
    }

    confirmCancel() {
        this.cancelService.confirmCancel(this.route);
    }

    private sign() {
        let that = this;
        // Enable spin
        this.spinnerService.show('spinner');

        this.errorMsg = undefined;

        console.debug('Current Signer Status is: ' + this.signersService.currentSigner.externalSignerStatus +
            ' has already validate certificate data: ' + this.rootScope.get().isCertificateApprovalDone);

        switch (this.signersService.currentSigner.externalSignerStatus) {
            case ExternalSignerStatusEnum.Signatures:
                this.http.signDocuments(this.sessionId, this.signersService.currentSigner.extSignerId).toPromise().then(function (response) {

                    // Disable the spin
                    that.spinnerService.hide('spinner');

                    if (response?.success) {
                        // If no error
                        that.rootScope.get().isCertificateApprovalDone = false; // This variable is set to false on the agreements controller
                        that.redirectionService.session(that.route, that.sessionId);
                    } else if (!response?.success && response?.error?.errorCode === 70) {
                        // concurrent signature already in progress, launch sign again in 5 seconds
                        setTimeout(function () {
                            that.sign();
                        }, that.delay)
                    } else if (!response?.success && response?.error?.errorCode === 71) {
                        // concurrent signature finished
                        that.redirectionService.session(that.route, that.sessionId);
                    } else if (!response?.success && response?.error?.errorCode === 80) {
                        // If analysis result in error
                        console.warn('The analysis result is in error: ', response.error.msg);
                        that.errorMsg = 'error.analysis';
                        that.spinnerService.hide('spinner');
                    } else if (!response?.success && response?.error?.errorCode === 81) {
                        // If analysis still in progress
                        console.warn('The analysis still in progress: ', response.error.msg);
                        that.errorMsg = 'error.analysis.pending';
                        that.spinnerService.hide('spinner');
                    } else {
                        // If process error
                        console.warn(response?.error);
                        that.errorMsg = 'wait.content.retry';
                    }
                }, function (error) {
                    // If communication error
                    console.warn(error);
                    that.errorMsg = 'wait.content.retry';
                    that.spinnerService.hide('spinner');
                });
                break;
            case ExternalSignerStatusEnum.CertificateApproval:
                if (!this.rootScope.get().isCertificateApprovalDone) {
                    if (sessionStorage.getItem('sessionType') === 'smartphone') {
                        console.debug('Certificate validation not done yet for smartphone');
                    } else {
                        console.debug('Certificate validation not done yet');
                    }
                    //Call validation endpoint
                    this.http.validate(this.sessionId, this.signersService.currentSigner.extSignerId, this.sessionService.info.extSubscriberId).toPromise().then(function (response) {
                        console.info('validing certificateapproval');
                        if (response?.success) {
                            // Certificate validation done
                            that.rootScope.get().isCertificateApprovalDone = true; // This variable is set to false on the agreements controller
                            console.debug('Current Signer Status is: ' + that.signersService.currentSigner.externalSignerStatus +
                                ' has already validate certificate data: ' + that.rootScope.get().isCertificateApprovalDone);
                            that.redirectionService.session(that.route, that.sessionId);
                        } else if (!response?.success && response?.error?.errorCode === 70) {
                            // concurrent signature already in progress, launch sign again in 5 seconds
                            setTimeout(function () {
                                that.sign();
                            }, that.delay);
                        } else if (!response?.success && response?.error?.errorCode === 71) {
                            // concurrent signature finished
                            that.redirectionService.session(that.route, that.sessionId);
                        } else if (!response?.success && response?.error?.errorCode === 80) {
                            // If analysis result in error
                            console.warn('The analysis result is in error: ', response.error.msg);
                            that.errorMsg = 'error.analysis';
                            that.spinnerService.hide('spinner');
                        } else if (!response?.success && response?.error?.errorCode === 81) {
                            // If analysis still in progress
                            console.warn('The analysis still in progress: ', response.error.msg);
                            that.errorMsg = 'error.analysis.pending';
                            that.spinnerService.hide('spinner');
                        } else {
                            console.warn('An error occurred when validation dn information: ', response?.error?.msg);
                            that.errorMsg = 'error.signature';
                            that.spinnerService.hide('spinner');
                        }
                    }, function (error) {
                        console.warn('An error occurred when validation dn information: ', error);
                    });
                } else {
                    console.debug('Certificate validation already done - check signer status');
                    this.signersService.checkStatusLoop(this.route, this.sessionId);
                }
                break;
            default:
                break;
        }
    };

}
